import { Outlet } from 'react-router';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import {
  DocumentScanner,
  Message,
  RouteOutlined,
  LocalGasStation,
  Logout as LogoutIcon,
  GpsFixed,
  InfoOutlined,
  MoreHoriz,
  TrackChanges,
  DownloadForOffline,
  InsertChart,
  AlternateEmailOutlined,
  EditNote,
  Settings,
  ShareLocationOutlined,
  Attribution,
} from '@mui/icons-material';
import { Badge } from '@mui/material';

import { PopupType } from '@/types';
import { ContactsPopup, MessagesPopup, More, TrackingPopup, RoutesPopup, StreamChatPopup } from '@/components';
import { RefuelsPopup } from '@/features/refuels';
import { AppErrorBoundary } from '@/shared/ui/app-error-boundary/AppErrorBoundary';
import ReportPopup from '@/components/report/ReportPopup';
import StreamChatMenuIcon from '@/components/stream-chat/StreamChatMenuIcon';
import TachographPopup from '@/components/tachograph/fileDownload/TachographPopup';
import { NewFeatureBadge } from '@/components/common/NewFeatureBadge';
import FeedbackForm from '@/components/contacts/FeedbackForms';
import { TachographDiagramPopup } from '@/components/tachograph/diagram/TachographDiagramPopup';
import SysinfoPopup from '@/components/sysinfo/SysinfoPopup';
import EtransportIcon from '@/components/etransport/utils/EtransportSvgIcons';
import EtransportPopup from '@/components/etransport/Etransport';
import { Map } from '@/components';
import { Logout } from './Logout';
import { MinimalUser } from '@/generated/model';
import SharePopup from '@/components/tracking/SharePopup';
import SysAdminPopup from '@/components/sysadmin/SysAdminPopup';
import ProfilePopup from '@/components/settings/ProfilePopup';
import TargetAttributesPopup from '@/components/targetAttributes/TargetAttributesPopup';
import ShareableLinkPopup from '@/components/shareableLinks/ShareableLinksPopup';
import { UserAccessControlPopup, userAccessRoutes } from '@/features/user-access-control';

const newMessageCount = 0;

export function getAdditionalElements(
  closePopup: () => Promise<void>,
  isBottom: boolean,
  hasTachograph?: boolean,
  showUserAccessControl = isBottom
): PopupType[] {
  const userAccessControlElement = (
    <UserAccessControlPopup closePopup={closePopup} data-qa-element-id="user-access-control-popup" />
  );

  const additionalElements: PopupType[] = [
    {
      title: 'mainMenu.reports',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-01">
          <DocumentScanner fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <ReportPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      requiredExtraFeature: 'extrafeature_pluto_report_tab',
      dataQaElementId: 'more-list-item-reports',
      level: 1,
      path: '/reports',
    },
    {
      title: 'mainMenu.shareableLinks',
      icon: (
        <NewFeatureBadge releaseDate="2026-05-01">
          <ShareLocationOutlined fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <ShareableLinkPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'more-list-item-shareable-links',
      level: 2,
      path: 'shareablelinks',
    },
    {
      title: 'mainMenu.etransport',
      icon: (
        <NewFeatureBadge releaseDate="2026-02-20">
          <EtransportIcon fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <EtransportPopup close={close} data-qa-element-id="etransport-popup" />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2,
      path: isBottom ? '/etransport' : 'etransport',
      requiredExtraFeature: 'extrafeature_pluto_etransport',
      dataQaElementId: 'more-list-item-etransport',
    },
    ...(showUserAccessControl
      ? [
          {
            title: 'mainMenu.userAccessControl',
            icon: <Attribution fontSize="small" />,
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: true,
            level: 1,
            path: userAccessRoutes.root,
            activeOnSubRoutes: true,
            requiredExtraFeature: 'user_access_control',
            dataQaElementId: 'more-list-item-user-access-control',
          },
          {
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: false,
            level: 1,
            path: userAccessRoutes.userDetailsPattern,
            hidden: true,
            requiredExtraFeature: 'extrafeature_pluto_user_access_control',
          },
          {
            element: userAccessControlElement,
            showToken: false,
            mainFunction: true,
            showMobile: false,
            level: 1,
            path: userAccessRoutes.userFormPattern,
            hidden: true,
            requiredExtraFeature: 'extrafeature_pluto_user_access_control',
          },
        ]
      : []),
    {
      title: 'mainMenu.settings',
      icon: (
        <NewFeatureBadge releaseDate="2026-04-30">
          <Settings fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-settings',
      children: profileSubmenu(closePopup, isBottom),
      level: -1,
      path: '/settings',
    },
    {
      title: 'mainMenu.contacts',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-contacts',
      children: contactSubmenu(closePopup, isBottom),
      level: -1,
      path: '/contacts',
    },
    {
      title: 'mainMenu.logout',
      icon: <LogoutIcon fontSize={isBottom ? 'small' : 'medium'} />,
      element: <Logout />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-logout',
      level: 1,
      path: '/logout',
    },
    {
      element: <TargetAttributesPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      show: () => false,
      hidden: true,
      level: 1,
      path: '/targetattreditor',
    },
  ] satisfies PopupType[];

  if (hasTachograph) {
    additionalElements.unshift({
      title: 'mainMenu.tachograph',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <TrackChanges fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'more-list-item-tachograph',
      children: tachographSubmenu(closePopup, isBottom),
      level: 1,
      path: '/tachograph',
    });
  }

  return additionalElements;
}

function tachographSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.tachographSubmenu.fileDownload',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <DownloadForOffline fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <TachographPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'tachograph_file_download_menu_item',
      level: 1,
      path: '/tachograph/download',
    },
    {
      title: 'mainMenu.tachographSubmenu.chart',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <InsertChart fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <TachographDiagramPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'tachograph_chart_menu_item',
      level: 1,
      path: '/tachograph/chart',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function contactSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.contactSubmenu.contactInformationTitle',
      icon: <AlternateEmailOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ContactsPopup closePopup={closePopup} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'contact_information_menu_item',
      level: 2,
      path: 'contact',
    },
    {
      title: 'mainMenu.contactSubmenu.feedback',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-20">
          <EditNote fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <FeedbackForm closePopup={closePopup} />,
      showToken: true,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'feedback_menu_item',
      level: 2,
      path: 'feedback',
    },
    {
      title: 'mainMenu.systemInformation',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysinfoPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemInfo',
      level: 2,
      path: 'sysinfo',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function profileSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.profile',
      icon: (
        <NewFeatureBadge releaseDate="2026-04-30">
          <Settings fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <ProfilePopup closePopup={closePopup} data-qa-element-id="settings-popup" />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemSettings',
      level: 2,
      path: 'profile',
    },
    {
      title: 'systemInfo.system',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysAdminPopup closePopup={closePopup} />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemInfo',
      level: 2,
      path: 'sysadmin',
    },
  ] satisfies PopupType[];

  return subMenu;
}

export function popupDefs({
  closePopup,
  isBottom,
  user,
}: {
  closePopup: () => Promise<void>;
  isBottom: boolean;
  user: MinimalUser;
}): PopupType[] {
  // FIXME ez igy nem lazy loading ?
  const arr: PopupType[] = [
    {
      title: 'mainMenu.tracking',
      icon: <GpsFixed fontSize={isBottom ? 'small' : 'medium'} />,
      element: (
        <>
          <Outlet />
          <AppErrorBoundary boundaryId="tracking-map">
            <Map />
          </AppErrorBoundary>
        </>
      ), // ? az jo h ebben van az Outlet ?
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/tracking',
      navigateTo: '/tracking/select',
    },
    {
      element: (
        <AppErrorBoundary boundaryId="tracking-workflow">
          <TrackingPopup data-qa-element-id="tracking-popup" />
          <Outlet />
        </AppErrorBoundary>
      ),
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/tracking/select',
      hidden: true,
    },
    {
      element: <SharePopup />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 3,
      path: '/tracking/select/share',
      hidden: true,
    },
    {
      title: 'mainMenu.routes',
      icon: <RouteOutlined fontSize={isBottom ? 'small' : 'medium'} sx={{ transform: 'rotate(90deg)' }} />,
      element: (
        <>
          <Outlet />
          <AppErrorBoundary boundaryId="routes-map">
            <Map />
          </AppErrorBoundary>
        </>
      ), // ? az jo h ebben van az Outlet ?,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/routes',
      navigateTo: '/routes/select',
    },
    {
      element: (
        <AppErrorBoundary boundaryId="routes-workflow">
          <RoutesPopup data-qa-element-id="routes-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/routes/select',
      hidden: true,
    },
    {
      title: 'mainMenu.messages',
      icon:
        newMessageCount > 0 ? (
          <Badge color="secondary" variant="dot">
            <Message fontSize={isBottom ? 'small' : 'medium'} />
          </Badge>
        ) : (
          <Message fontSize={isBottom ? 'small' : 'medium'} />
        ),
      element: (
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <AppErrorBoundary boundaryId="messages-workflow" onReset={reset}>
              <MessagesPopup closePopup={closePopup} data-qa-element-id="messages-popup" />
            </AppErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: MinimalUser, chatId?: string) =>
        !(user?.extraFeatures?.includes('extrafeature_pluto_getstreamio_chat') ?? false) || !chatId,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/messages' : 'messages', // FIXME jobb lenne ha ez is /-el kezdodne mint minden mas
    },
    {
      title: 'mainMenu.streamMessages',
      icon: <StreamChatMenuIcon isBottom={isBottom} />,
      element: (
        <AppErrorBoundary boundaryId="stream-chat-workflow">
          <StreamChatPopup closePopup={closePopup} data-qa-element-id="stream-messages-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: MinimalUser, chatId?: string) =>
        !!user && (user.extraFeatures?.includes('extrafeature_pluto_getstreamio_chat') ?? false) && !!chatId,
      requiredExtraFeature: 'extrafeature_pluto_getstreamio_chat',
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/chat' : 'chat', // FIXME jobb lenne ha ez is /-el kezdodne mint minden mas
    },
    {
      title: 'mainMenu.refuels',
      icon: <LocalGasStation fontSize={isBottom ? 'small' : 'medium'} />,
      element: (
        <AppErrorBoundary boundaryId="refuels-popup">
          <RefuelsPopup closePopup={closePopup} data-qa-element-id="refuels-popup" />
        </AppErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/refuels' : 'refuels',
    },
  ] satisfies PopupType[];

  let additionalElement: PopupType[];
  if (isBottom) {
    additionalElement = [
      {
        title: 'mainMenu.more',
        icon: (
          <NewFeatureBadge releaseDate="2026-01-20">
            <MoreHoriz fontSize="small" />
          </NewFeatureBadge>
        ),
        element: (
          <More closePopup={closePopup} hasTachograph={user.hasTachograph} data-qa-element-id="more-menu-list" />
        ),
        showToken: true,
        mainFunction: false,
        showMobile: true,
        level: 2,
        path: 'more',
        navigateTo: 'more',
      },
    ] satisfies PopupType[];
  } else {
    additionalElement = getAdditionalElements(closePopup, isBottom, user.hasTachograph);
  }

  return arr.concat(additionalElement);
}
